import React, { useEffect, useState } from "react";
import VsOpsLayout from "./VsOpsLayout";
import { VsOpsRequireAuth } from "./VsOpsLoginPage";
import { vsOpsApi } from "./api";
import { formatRubPrice, formatUsdtPrice } from "../visoria/helpers";

function formatOrderAmount(item) {
  if (item.paymentMethod === "CRYPTO") {
    return formatUsdtPrice(item.profit.amountUsdt);
  }
  return formatRubPrice(item.profit.amountRub);
}

function formatOrderProfit(item) {
  if (item.paymentMethod === "CRYPTO") {
    return formatUsdtPrice(item.profit.profitUsdt);
  }
  return formatRubPrice(item.profit.profitRub);
}

function formatDate(value) {
  if (!value) {
    return "—";
  }
  return new Date(value).toLocaleString("ru-RU");
}

function VsOpsSalesPage() {
  const [filters, setFilters] = useState({
    orderId: "",
    email: "",
    code: "",
    storeId: "",
    regionId: "",
    from: "",
    to: "",
    includePending: false,
  });
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [visoriaBalanceLabel, setVisoriaBalanceLabel] = useState("");
  const [visoriaBalanceError, setVisoriaBalanceError] = useState("");
  const [returnInputs, setReturnInputs] = useState({});
  const [returnReasonInputs, setReturnReasonInputs] = useState({});
  const [returnLoading, setReturnLoading] = useState({});
  const [returnMessages, setReturnMessages] = useState({});
  const [abortLoading, setAbortLoading] = useState({});
  const [abortMessages, setAbortMessages] = useState({});

  const loadBalance = async () => {
    try {
      const balance = await vsOpsApi.getVisoriaBalance();
      setVisoriaBalanceLabel(balance.label || "");
      setVisoriaBalanceError("");
    } catch (balanceError) {
      setVisoriaBalanceLabel("");
      setVisoriaBalanceError(balanceError.message || "баланс недоступен");
    }
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      // includePending must be sent ONLY when checked,
      // otherwise backend treats non-empty strings like "false" as truthy.
      const params = Object.fromEntries(
        Object.entries(filters)
          .filter(([key, value]) => {
            if (key === "includePending") {
              return Boolean(value);
            }
            return String(value).trim();
          })
          .map(([key, value]) => [
            key,
            key === "includePending" ? "1" : value,
          ]),
      );
      const [result] = await Promise.all([
        vsOpsApi.getSales(params),
        loadBalance(),
      ]);
      setData(result);
    } catch (loadError) {
      setError(loadError.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleReturnCard = async (orderId) => {
    const keyCode = String(returnInputs[orderId] || "").trim();
    if (!keyCode) {
      setReturnMessages((current) => ({
        ...current,
        [orderId]: { type: "error", text: "Вставьте код карты для возврата" },
      }));
      return;
    }

    setReturnLoading((current) => ({ ...current, [orderId]: true }));
    setReturnMessages((current) => ({ ...current, [orderId]: null }));
    try {
      await vsOpsApi.returnCard(orderId, keyCode, returnReasonInputs[orderId] || "");
      setReturnInputs((current) => ({ ...current, [orderId]: "" }));
      setReturnReasonInputs((current) => ({ ...current, [orderId]: "" }));
      setReturnMessages((current) => ({
        ...current,
        [orderId]: { type: "success", text: "Карта возвращена на локальный склад" },
      }));
      await load();
    } catch (returnError) {
      setReturnMessages((current) => ({
        ...current,
        [orderId]: {
          type: "error",
          text: returnError.message || "Не удалось вернуть карту",
        },
      }));
    } finally {
      setReturnLoading((current) => ({ ...current, [orderId]: false }));
    }
  };

  const handleAbortFulfillment = async (orderId) => {
    const confirmed = window.confirm(
      `Остановить выдачу по заказу ${orderId}?\n\n` +
        "Worker и webhook Visoria больше не смогут перевести заказ в SUCCESS " +
        "(код клиенту автоматически не уйдёт). Деньги возвращайте вручную.",
    );
    if (!confirmed) {
      return;
    }

    setAbortLoading((current) => ({ ...current, [orderId]: true }));
    setAbortMessages((current) => ({ ...current, [orderId]: null }));
    try {
      await vsOpsApi.abortFulfillment(
        orderId,
        "Выдача отменена админом перед рефандом",
      );
      setAbortMessages((current) => ({
        ...current,
        [orderId]: {
          type: "ok",
          text: "Выдача остановлена (CANCELLED). Можно делать рефанд.",
        },
      }));
      await load();
    } catch (abortError) {
      setAbortMessages((current) => ({
        ...current,
        [orderId]: {
          type: "error",
          text: abortError.message || "Не удалось остановить выдачу",
        },
      }));
    } finally {
      setAbortLoading((current) => ({ ...current, [orderId]: false }));
    }
  };

  return (
    <VsOpsRequireAuth>
      <VsOpsLayout
        title="Продажи"
        titleAside={
          <span
            className={`vs-ops-visoria-balance${
              visoriaBalanceError ? " vs-ops-visoria-balance--error" : ""
            }`}
            title={visoriaBalanceError || undefined}
          >
            {visoriaBalanceLabel
              ? `баланс visoria ${visoriaBalanceLabel}`
              : visoriaBalanceError
                ? "баланс visoria —"
                : "баланс visoria …"}
          </span>
        }
      >
        <form
          className="vs-ops-filters"
          onSubmit={(event) => {
            event.preventDefault();
            load();
          }}
        >
          <label>
            № заказа
            <input
              value={filters.orderId}
              onChange={(event) =>
                setFilters((current) => ({ ...current, orderId: event.target.value }))
              }
              placeholder="os_1786372592885_a47e7032"
            />
          </label>
          <label>
            Email
            <input
              value={filters.email}
              onChange={(event) =>
                setFilters((current) => ({ ...current, email: event.target.value }))
              }
              placeholder="customer@mail.ru"
            />
          </label>
          <label>
            Код
            <input
              value={filters.code}
              onChange={(event) =>
                setFilters((current) => ({ ...current, code: event.target.value }))
              }
              placeholder="XXXX-XXXX"
            />
          </label>
          <label>
            Магазин
            <input
              value={filters.storeId}
              onChange={(event) =>
                setFilters((current) => ({ ...current, storeId: event.target.value }))
              }
              placeholder="nintendo"
            />
          </label>
          <label>
            Регион
            <input
              value={filters.regionId}
              onChange={(event) =>
                setFilters((current) => ({ ...current, regionId: event.target.value }))
              }
              placeholder="usd"
            />
          </label>
          <label>
            С
            <input
              type="date"
              value={filters.from}
              onChange={(event) =>
                setFilters((current) => ({ ...current, from: event.target.value }))
              }
            />
          </label>
          <label>
            По
            <input
              type="date"
              value={filters.to}
              onChange={(event) =>
                setFilters((current) => ({ ...current, to: event.target.value }))
              }
            />
          </label>
          <label className="vs-ops-checkbox">
            <input
              type="checkbox"
              checked={filters.includePending}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  includePending: event.target.checked,
                }))
              }
            />
            Показать неоплаченные счета
          </label>
          <button type="submit" className="vs-ops-btn" disabled={loading}>
            Найти
          </button>
        </form>

        {error ? <p className="vs-ops-error">{error}</p> : null}
        <p className="vs-ops-muted">Найдено: {data.total}</p>

        <div className="vs-ops-mobile-list">
          {data.items.map((item) => (
            <article key={item.orderId} className="vs-ops-mobile-card">
              <div className="vs-ops-mobile-card-head">
                <strong>{formatDate(item.paymentCompletedAt || item.createdAt)}</strong>
                <span>{formatOrderAmount(item)}</span>
              </div>
              <dl className="vs-ops-mobile-meta">
                <div>
                  <dt>Email</dt>
                  <dd>{item.email}</dd>
                </div>
                <div>
                  <dt>Магазин</dt>
                  <dd>
                    {item.storeId} / {item.regionId}
                  </dd>
                </div>
                <div>
                  <dt>Товар</dt>
                  <dd>
                    {item.productName}
                    <span className="vs-ops-muted"> ×{item.quantity}</span>
                  </dd>
                </div>
                <div>
                  <dt>Оплата</dt>
                  <dd>{item.paymentMethod === "CRYPTO" ? "Крипта" : "СБП"}</dd>
                </div>
                <div>
                  <dt>Прибыль</dt>
                  <dd>{formatOrderProfit(item)}</dd>
                </div>
                <div>
                  <dt>Коды</dt>
                  <dd className="vs-ops-codes-cell">
                    {item.keys?.length
                      ? item.keys.map((keyItem) => (
                          <code key={keyItem.key}>{keyItem.key}</code>
                        ))
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt>Выдача</dt>
                  <dd>
                    <span>{item.fulfillmentStatus || "—"}</span>
                    {item.fulfillmentError ? (
                      <span className="vs-ops-error">{item.fulfillmentError}</span>
                    ) : null}
                  </dd>
                </div>
              </dl>
              <div className="vs-ops-stack">
                <input
                  value={returnInputs[item.orderId] || ""}
                  onChange={(event) =>
                    setReturnInputs((current) => ({
                      ...current,
                      [item.orderId]: event.target.value,
                    }))
                  }
                  placeholder="Вставьте код для возврата"
                />
                <input
                  value={returnReasonInputs[item.orderId] || ""}
                  onChange={(event) =>
                    setReturnReasonInputs((current) => ({
                      ...current,
                      [item.orderId]: event.target.value,
                    }))
                  }
                  placeholder="Причина возврата (необязательно)"
                />
                <button
                  type="button"
                  className="vs-ops-btn"
                  disabled={Boolean(returnLoading[item.orderId])}
                  onClick={() => handleReturnCard(item.orderId)}
                >
                  Вернуть
                </button>
                {item.paymentStatus === "SUCCESS" &&
                item.fulfillmentStatus !== "SUCCESS" &&
                item.fulfillmentStatus !== "CANCELLED" ? (
                  <button
                    type="button"
                    className="vs-ops-btn vs-ops-btn-danger"
                    disabled={Boolean(abortLoading[item.orderId])}
                    onClick={() => handleAbortFulfillment(item.orderId)}
                  >
                    Остановить выдачу
                  </button>
                ) : null}
                {abortMessages[item.orderId]?.text ? (
                  <p
                    className={
                      abortMessages[item.orderId]?.type === "error"
                        ? "vs-ops-error"
                        : "vs-ops-muted"
                    }
                  >
                    {abortMessages[item.orderId].text}
                  </p>
                ) : null}
                {returnMessages[item.orderId]?.text ? (
                  <p
                    className={
                      returnMessages[item.orderId]?.type === "error"
                        ? "vs-ops-error"
                        : "vs-ops-muted"
                    }
                  >
                    {returnMessages[item.orderId].text}
                  </p>
                ) : null}
                {item.hasReturnedCards ? (
                  <div className="vs-ops-muted">
                    Возвращено: {item.returnedCardCount}
                    {item.returnedKeys?.map((returned) => (
                      <div key={returned.id}>
                        <code>{returned.keyCode}</code>
                        {returned.resoldOrderId
                          ? ` → перепродана в ${returned.resoldOrderId}`
                          : " → на локальном складе"}
                      </div>
                    ))}
                  </div>
                ) : null}
                {item.resoldFromOrderId ? (
                  <p className="vs-ops-muted">
                    Карта из возврата заказа {item.resoldFromOrderId}
                  </p>
                ) : null}
              </div>
            </article>
          ))}
        </div>

        <div className="vs-ops-table-wrap vs-ops-table-wrap-desktop">
          <table className="vs-ops-table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Email</th>
                <th>Магазин</th>
                <th>Регион</th>
                <th>Товар</th>
                <th>Оплата</th>
                <th>Коды</th>
                <th>Выдача</th>
                <th>Возврат / перепродажа</th>
                <th>Сумма</th>
                <th>Прибыль</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.orderId}>
                  <td>{formatDate(item.paymentCompletedAt || item.createdAt)}</td>
                  <td>{item.email}</td>
                  <td>{item.storeId}</td>
                  <td>{item.regionId}</td>
                  <td>
                    {item.productName}
                    <span className="vs-ops-muted"> ×{item.quantity}</span>
                  </td>
                  <td>{item.paymentMethod === "CRYPTO" ? "Крипта" : "СБП"}</td>
                  <td className="vs-ops-codes-cell">
                    {item.keys?.length
                      ? item.keys.map((keyItem) => (
                          <code key={keyItem.key}>{keyItem.key}</code>
                        ))
                      : "—"}
                  </td>
                  <td>
                    <div className="vs-ops-stack">
                      <span>{item.fulfillmentStatus || "—"}</span>
                      {item.fulfillmentError ? (
                        <span className="vs-ops-error">{item.fulfillmentError}</span>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    <div className="vs-ops-stack">
                      <input
                        value={returnInputs[item.orderId] || ""}
                        onChange={(event) =>
                          setReturnInputs((current) => ({
                            ...current,
                            [item.orderId]: event.target.value,
                          }))
                        }
                        placeholder="Вставьте код для возврата"
                      />
                      <input
                        value={returnReasonInputs[item.orderId] || ""}
                        onChange={(event) =>
                          setReturnReasonInputs((current) => ({
                            ...current,
                            [item.orderId]: event.target.value,
                          }))
                        }
                        placeholder="Причина возврата (необязательно)"
                      />
                      <button
                        type="button"
                        className="vs-ops-btn"
                        disabled={Boolean(returnLoading[item.orderId])}
                        onClick={() => handleReturnCard(item.orderId)}
                      >
                        Вернуть
                      </button>
                      {item.paymentStatus === "SUCCESS" &&
                      item.fulfillmentStatus !== "SUCCESS" &&
                      item.fulfillmentStatus !== "CANCELLED" ? (
                        <button
                          type="button"
                          className="vs-ops-btn vs-ops-btn-danger"
                          disabled={Boolean(abortLoading[item.orderId])}
                          onClick={() => handleAbortFulfillment(item.orderId)}
                        >
                          Остановить выдачу
                        </button>
                      ) : null}
                      {abortMessages[item.orderId]?.text ? (
                        <p
                          className={
                            abortMessages[item.orderId]?.type === "error"
                              ? "vs-ops-error"
                              : "vs-ops-muted"
                          }
                        >
                          {abortMessages[item.orderId].text}
                        </p>
                      ) : null}
                      {returnMessages[item.orderId]?.text ? (
                        <p
                          className={
                            returnMessages[item.orderId]?.type === "error"
                              ? "vs-ops-error"
                              : "vs-ops-muted"
                          }
                        >
                          {returnMessages[item.orderId].text}
                        </p>
                      ) : null}
                      {item.hasReturnedCards ? (
                        <div className="vs-ops-muted">
                          Возвращено: {item.returnedCardCount}
                          {item.returnedKeys?.map((returned) => (
                            <div key={returned.id}>
                              <code>{returned.keyCode}</code>
                              {returned.resoldOrderId
                                ? ` → перепродана в ${returned.resoldOrderId}`
                                : " → на локальном складе"}
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {item.resoldFromOrderId ? (
                        <p className="vs-ops-muted">
                          Карта из возврата заказа {item.resoldFromOrderId}
                        </p>
                      ) : null}
                    </div>
                  </td>
                  <td>{formatOrderAmount(item)}</td>
                  <td>{formatOrderProfit(item)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </VsOpsLayout>
    </VsOpsRequireAuth>
  );
}

export default VsOpsSalesPage;
